import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './Localization'; // Import the custom hook

const Hero = ({ games }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useLanguage(); // Use language context

  // Keep the image and its name together so the caption never desyncs
  const slides = games.filter((game) => game.image);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [slides.length]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
  };
  const item = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-brand-500 via-brand-600 to-emerald-800 bg-gradient-animated animate-gradient-x pt-28 md:pt-32">
      {/* Decorative animated blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-20 w-80 h-80 rounded-full bg-emerald-300/20 blur-3xl animate-blob [animation-delay:3s]" />
        <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-teal-200/10 blur-3xl animate-blob [animation-delay:6s]" />
      </div>

      <div className="container relative mx-auto px-4 pb-28 md:pb-36">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <motion.div variants={container} initial="hidden" animate="show">
            <motion.h1
              variants={item}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-sm"
            >
              {t('title')}
            </motion.h1>
            <motion.p
              variants={item}
              className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-8 max-w-xl"
            >
              {t('description')}
            </motion.p>
            <motion.div variants={item} className="flex flex-wrap gap-4">
              <a
                href="https://apps.apple.com/developer/summit-games/id1562450250?see-all=i-phonei-pad-apps"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white text-brand-700 font-semibold px-7 py-3.5 rounded-full shadow-lg hover:shadow-glow hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 transition-transform group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                {t('appStore')}
              </a>
              <a
                href="https://play.google.com/store/apps/dev?id=4664007975596285790"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gray-900 text-white font-semibold px-7 py-3.5 rounded-full shadow-lg hover:shadow-glow hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 transition-transform group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                {t('googlePlay')}
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative w-full flex flex-col items-center justify-center"
          >
            <div className="relative w-full flex items-center justify-center h-[340px] sm:h-[400px] animate-float">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  src={slides[currentIndex].image}
                  alt={slides[currentIndex].name}
                  className="w-full max-w-md object-contain mx-auto drop-shadow-2xl"
                  initial={{ opacity: 0, x: 40, rotate: 2 }}
                  animate={{ opacity: 1, x: 0, rotate: 0 }}
                  exit={{ opacity: 0, x: -40, rotate: -2 }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                />
              </AnimatePresence>
            </div>

            {/* Carousel indicators */}
            <div className="flex gap-2 mt-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  aria-label={`Show ${slides[i].name}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === currentIndex ? 'w-6 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Smooth wave divider into the next section */}
      <div className="absolute bottom-0 left-0 w-full leading-none" aria-hidden="true">
        <svg
          className="w-full h-[60px] sm:h-[90px]"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#f8fafc"
            d="M0,64L60,69.3C120,75,240,85,360,80C480,75,600,53,720,48C840,43,960,53,1080,58.7C1200,64,1320,64,1380,64L1440,64L1440,120L0,120Z"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
